// src/pages/reportes/components/ReporteGerencial.tsx
import { Card, Spinner, Badge } from '@/components/ui';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  AlertCircle,
  Users,
  Clock,
  CheckCircle2,
  Target,
} from 'lucide-react';
import { useReporteGerencial } from '@/hooks/queries/useReportes';
import { PeriodoReporte } from '@/types/reporte';
import type { ReporteGerencial as ReporteGerencialType } from '@/types/reporte';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface ReporteGerencialProps {
  periodo: PeriodoReporte;
}

interface KPICardProps {
  titulo: string;
  valor: number | string;
  valorAnterior?: number;
  variacion?: number;
  direccion?: string;
  formato?: 'numero' | 'porcentaje' | 'tiempo';
  icono: React.ReactNode;
  colorIcono: string;
}

function KPICard({
  titulo,
  valor,
  valorAnterior,
  variacion,
  direccion,
  formato = 'numero',
  icono,
  colorIcono,
}: KPICardProps) {
  const formatValue = (v: number | string) => {
    if (typeof v === 'string') return v;
    switch (formato) {
      case 'porcentaje':
        return `${v.toFixed(1)}%`;
      case 'tiempo':
        return `${Math.round(v)} min`;
      default:
        return v.toLocaleString();
    }
  };

  const getVariacionIcon = () => {
    if (!direccion) return <Minus className="h-4 w-4 text-gray-400" />;
    if (direccion === 'up') return <TrendingUp className="h-4 w-4 text-green-500" />;
    if (direccion === 'down') return <TrendingDown className="h-4 w-4 text-red-500" />;
    return <Minus className="h-4 w-4 text-gray-400" />;
  };

  const getVariacionColor = () => {
    if (!direccion) return 'text-gray-500';
    if (direccion === 'up') return 'text-green-600';
    if (direccion === 'down') return 'text-red-600';
    return 'text-gray-500';
  };

  return (
    <Card className="p-4">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${colorIcono}`}>{icono}</div>
          <div>
            <p className="text-sm text-gray-500">{titulo}</p>
            <p className="text-2xl font-bold">{formatValue(valor)}</p>
          </div>
        </div>
        {variacion !== undefined && (
          <div className="text-right">
            <div className="flex items-center gap-1">
              {getVariacionIcon()}
              <span className={`text-sm font-medium ${getVariacionColor()}`}>
                {variacion > 0 ? '+' : ''}{variacion.toFixed(1)}%
              </span>
            </div>
            {valorAnterior !== undefined && (
              <p className="text-xs text-gray-400">vs {formatValue(valorAnterior)}</p>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

export function ReporteGerencial({ periodo }: ReporteGerencialProps) {
  const { data: response, isLoading, error } = useReporteGerencial(periodo);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="p-6">
        <div className="text-center text-red-500">
          <AlertCircle className="h-12 w-12 mx-auto mb-4" />
          <p>Error al cargar el reporte gerencial</p>
        </div>
      </Card>
    );
  }

  if (!response) {
    return (
      <Card className="p-6">
        <p className="text-center text-gray-500">No hay datos disponibles para el periodo seleccionado</p>
      </Card>
    );
  }

  // Extraer datos del reporte
  const reporte = response.datos as unknown as ReporteGerencialType | undefined;

  if (!reporte) {
    return (
      <Card className="p-6">
        <p className="text-center text-gray-500">No hay datos disponibles para el periodo seleccionado</p>
      </Card>
    );
  }

  const formatFecha = (fecha: string) => {
    try {
      return format(new Date(fecha), 'dd MMM yyyy', { locale: es });
    } catch {
      return fecha;
    }
  };

  return (
    <div className="space-y-6">
      {/* Periodo info */}
      {reporte.periodo && (
        <Card className="p-4 bg-gray-50">
          <div className="flex items-center justify-between text-sm">
            <div>
              <span className="text-gray-500">Periodo actual: </span>
              <span className="font-medium">
                {formatFecha(reporte.periodo.actual?.inicio ?? '')} - {formatFecha(reporte.periodo.actual?.fin ?? '')}
              </span>
            </div>
            <div>
              <span className="text-gray-500">vs anterior: </span>
              <span className="font-medium">
                {formatFecha(reporte.periodo.anterior?.inicio ?? '')} - {formatFecha(reporte.periodo.anterior?.fin ?? '')}
              </span>
            </div>
          </div>
        </Card>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          titulo="Tickets Resueltos"
          valor={reporte.kpis?.tickets_resueltos?.valor ?? 0}
          valorAnterior={reporte.kpis?.tickets_resueltos?.anterior}
          variacion={reporte.kpis?.tickets_resueltos?.variacion}
          direccion={reporte.kpis?.tickets_resueltos?.direccion}
          icono={<CheckCircle2 className="h-5 w-5 text-green-600" />}
          colorIcono="bg-green-100"
        />
        <KPICard
          titulo="SLA Cumplido"
          valor={reporte.kpis?.sla_cumplido?.valor ?? 0}
          valorAnterior={reporte.kpis?.sla_cumplido?.anterior}
          variacion={reporte.kpis?.sla_cumplido?.variacion}
          direccion={reporte.kpis?.sla_cumplido?.direccion}
          formato="porcentaje"
          icono={<Target className="h-5 w-5 text-blue-600" />}
          colorIcono="bg-blue-100"
        />
        <KPICard
          titulo="Tiempo Promedio"
          valor={reporte.kpis?.tiempo_promedio_resolucion_min ?? 0}
          formato="tiempo"
          icono={<Clock className="h-5 w-5 text-purple-600" />}
          colorIcono="bg-purple-100"
        />
        <KPICard
          titulo="Horas Registradas"
          valor={reporte.kpis?.horas_registradas?.valor ?? 0}
          valorAnterior={reporte.kpis?.horas_registradas?.anterior}
          variacion={reporte.kpis?.horas_registradas?.variacion}
          direccion={reporte.kpis?.horas_registradas?.direccion}
          icono={<Clock className="h-5 w-5 text-orange-600" />}
          colorIcono="bg-orange-100"
        />
      </div>

      {/* Productividad del equipo */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Users className="h-5 w-5" />
          Productividad del Equipo
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left py-3 px-4 font-medium text-gray-700">Usuario</th>
                <th className="text-right py-3 px-4 font-medium text-gray-700">Productividad</th>
                <th className="text-right py-3 px-4 font-medium text-gray-700">Grafico</th>
              </tr>
            </thead>
            <tbody>
              {(reporte.equipo?.productividad ?? []).map((item, idx) => {
                const maxValor = Math.max(...(reporte.equipo?.productividad ?? []).map((p) => p.valor), 1);
                const porcentaje = (item.valor / maxValor) * 100;
                return (
                  <tr key={idx} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4">{item.usuario}</td>
                    <td className="py-3 px-4 text-right font-medium">{item.valor.toFixed(1)}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-blue-500 rounded-full"
                            style={{ width: `${porcentaje}%` }}
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {(!reporte.equipo?.productividad || reporte.equipo.productividad.length === 0) && (
                <tr>
                  <td colSpan={3} className="py-6 text-center text-gray-500">
                    Sin datos de productividad
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Carga de trabajo */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Carga de Trabajo</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left py-3 px-4 font-medium text-gray-700">Usuario</th>
                <th className="text-right py-3 px-4 font-medium text-gray-700">Tickets Asignados</th>
                <th className="text-right py-3 px-4 font-medium text-gray-700">Estado</th>
              </tr>
            </thead>
            <tbody>
              {(reporte.equipo?.carga_trabajo ?? []).map((item, idx) => {
                let badgeVariant: 'default' | 'primary' | 'danger' | 'warning' | 'success' = 'default';
                let estado = 'Normal';
                if (item.tickets > 10) {
                  badgeVariant = 'danger';
                  estado = 'Sobrecargado';
                } else if (item.tickets > 5) {
                  badgeVariant = 'warning';
                  estado = 'Alto';
                } else if (item.tickets < 2) {
                  badgeVariant = 'success';
                  estado = 'Bajo';
                }
                return (
                  <tr key={idx} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4">{item.usuario}</td>
                    <td className="py-3 px-4 text-right font-medium">{item.tickets}</td>
                    <td className="py-3 px-4 text-right">
                      <Badge variant={badgeVariant}>{estado}</Badge>
                    </td>
                  </tr>
                );
              })}
              {(!reporte.equipo?.carga_trabajo || reporte.equipo.carga_trabajo.length === 0) && (
                <tr>
                  <td colSpan={3} className="py-6 text-center text-gray-500">
                    Sin datos de carga de trabajo
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Horas por cliente */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Horas por Cliente</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left py-3 px-4 font-medium text-gray-700">Cliente</th>
                <th className="text-right py-3 px-4 font-medium text-gray-700">Horas</th>
                <th className="text-right py-3 px-4 font-medium text-gray-700">Distribucion</th>
              </tr>
            </thead>
            <tbody>
              {(reporte.clientes?.horas_por_cliente ?? []).map((item, idx) => {
                const totalHoras = (reporte.clientes?.horas_por_cliente ?? []).reduce(
                  (acc, c) => acc + c.horas,
                  0
                );
                const porcentaje = totalHoras > 0 ? (item.horas / totalHoras) * 100 : 0;
                return (
                  <tr key={idx} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4">{item.cliente}</td>
                    <td className="py-3 px-4 text-right font-medium">{item.horas.toFixed(1)}h</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-green-500 rounded-full"
                            style={{ width: `${porcentaje}%` }}
                          />
                        </div>
                        <span className="text-sm text-gray-500 w-12 text-right">
                          {porcentaje.toFixed(0)}%
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {(!reporte.clientes?.horas_por_cliente || reporte.clientes.horas_por_cliente.length === 0) && (
                <tr>
                  <td colSpan={3} className="py-6 text-center text-gray-500">
                    Sin datos de horas por cliente
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
